import { useRequiredContext } from "@redotech/react-util/context";
import { Flex } from "@redotech/redo-web/flex";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import * as styles from "../general.module.css";
import { TabNavigationContext } from "../tab-navigation-provider";

export function SettingSectionTabNav() {
  const { sectionRefs } = useRequiredContext(TabNavigationContext);
  const [activeSection, setActiveSection] = useState<string>("");

  const eleList = useMemo(() => {
    return Object.entries(sectionRefs)
      .filter(([, { ref }]) => ref !== null)
      .sort(([, { ref: refA }], [, { ref: refB }]) => {
        if (!refA || !refB) return 0;
        const topA = refA.getBoundingClientRect().top + window.scrollY;
        const topB = refB.getBoundingClientRect().top + window.scrollY;
        return topA - topB;
      });
  }, [sectionRefs]);

  useEffect(() => {
    const intersectingIds = new Set<string>();

    const findClosestSection = () => {
      let closestId = "";
      let closestDistance = Infinity;

      for (const id of intersectingIds) {
        const sectionData = sectionRefs[id];
        if (sectionData?.ref) {
          const rect = sectionData.ref.getBoundingClientRect();
          const distance = Math.abs(rect.top);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestId = id;
          }
        }
      }

      if (closestId) {
        setActiveSection(closestId);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = Object.entries(sectionRefs).find(
            ([, { ref }]) => ref === entry.target,
          )?.[0];

          if (id) {
            if (entry.isIntersecting) {
              intersectingIds.add(id);
            } else {
              intersectingIds.delete(id);
            }
          }
        }
        findClosestSection();
      },
      { threshold: 0, rootMargin: "-200px 0px -50% 0px" },
    );

    for (const { ref } of Object.values(sectionRefs)) {
      if (ref) {
        observer.observe(ref);
      }
    }

    return () => observer.disconnect();
  }, [sectionRefs]);

  return (
    <div className={styles.sideNavContainer}>
      <Flex dir="column" gap="none">
        {eleList.map(([id, { title, ref }]) => (
          <SettingSectionTabNavItem
            active={activeSection === id}
            elementRef={ref}
            id={id}
            key={id}
            title={title}
          />
        ))}
      </Flex>
    </div>
  );
}

function SettingSectionTabNavItem({
  title,
  id,
  elementRef,
  active,
}: {
  title: string;
  id: string;
  elementRef?: HTMLElement | null;
  active: boolean;
}) {
  const scrollToElement = () => {
    elementRef?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      className={classNames(styles.sideNavLink, { [styles.active]: active })}
      onClick={scrollToElement}
    >
      {title}
    </div>
  );
}
